import { useEffect, useMemo, useState } from 'react';
import { Modal, Radio } from 'antd';
import styled from 'styled-components';

import type { EpiComposition } from '../types/types';
import { text } from '../utils/epiHelpers';
import { useEpiContext } from '../context/EpiContext';

interface IProps {
  isOpen: boolean;
  /** The composition the user is trying to remove. */
  source: EpiComposition;
  /** Visible (non-toBeDeleted) sibling compositions in the same tab. */
  siblings: EpiComposition[];
  onConfirm: (targetId: string) => void;
  onCancel: () => void;
}

const MergeCompositionModal: React.FC<IProps> = ({
  isOpen,
  source,
  siblings,
  onConfirm,
  onCancel,
}) => {
  const { lang } = useEpiContext();
  // Sibling list excludes the source itself; in 2-composition case there's
  // only one entry and we pre-select it (no actual choice for the user).
  const options = useMemo(
    () => siblings.filter(c => c.id !== source.id),
    [siblings, source.id]
  );

  const [pickedId, setPickedId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setPickedId(options.length === 1 ? options[0].id : null);
  }, [isOpen, options]);

  const valid = pickedId !== null;

  return (
    <Modal
      open={isOpen}
      title={`Remove "${text(source.title, lang)}"`}
      okText="Yes, remove and merge"
      cancelText="Cancel"
      okButtonProps={{ disabled: !valid, danger: true }}
      onOk={() => pickedId && onConfirm(pickedId)}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Description>
        Every section and edit in <strong>{text(source.title, lang)}</strong>{' '}
        will be lost. Its medicinal products move to the document you choose and
        take on its content.
      </Description>
      {options.length === 1 ? (
        <SingleOption>
          Medicinal products will move to:{' '}
          <strong>{text(options[0].title, lang)}</strong>
        </SingleOption>
      ) : (
        <Radio.Group
          value={pickedId}
          onChange={e => setPickedId(e.target.value)}
        >
          <Vertical>
            {options.map(c => (
              <Radio key={c.id} value={c.id}>
                {text(c.title, lang)}
              </Radio>
            ))}
          </Vertical>
        </Radio.Group>
      )}
    </Modal>
  );
};

export default MergeCompositionModal;

const Description = styled.p`
  margin: 0 0 12px;
  font-size: 13px;
  color: #4b576a;
`;

const SingleOption = styled.div`
  font-size: 13px;
  padding: 8px 12px;
  background: #f5f5f5;
  border-radius: 6px;
`;

const Vertical = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
